import { CommonModule } from '@angular/common';
import { AuditDirectoryCellComponent } from '../../shared/audit/audit-directory-cell.component';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { forkJoin } from 'rxjs';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { WarehousingMasterType, companyOwnedWarehouses, masterDataMap, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingInboundReceipt, WarehousingInboundReceiptLineSummary, WarehousingItemSummary, WarehousingMasterData } from '../warehousing.model';
import { itemIdentityLabel, itemSizeLabel } from '../warehousing-item-display';
import { WarehousingPageHeaderComponent } from '../warehousing-page-header.component';
import { WarehousingSearchPanelComponent } from '../warehousing-search-panel.component';
import { WarehousingService } from '../warehousing.service';

@Component({
  selector: 'app-warehousing-stock-entry',
  standalone: true,
  imports: [AuditDirectoryCellComponent,
    CommonModule, ReactiveFormsModule, RouterModule, AutoCompleteModule, BreadcrumbModule, ButtonModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputTextModule, ProgressBarModule, SelectModule, TableModule,
    WarehousingPageHeaderComponent, WarehousingSearchPanelComponent
  ],
  templateUrl: './warehousing-stock-entry.component.html',
  styleUrl: './warehousing-stock-entry.component.css'
})
export class WarehousingStockEntryComponent implements OnInit {
  private fb = inject(FormBuilder);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private permissionService = inject(PermissionService);

  isLoading = false;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Receive Stock');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  entries: WarehousingInboundReceipt[] = [];
  private allEntries: WarehousingInboundReceipt[] = [];
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  masterData = masterDataMap();
  itemOptions: Array<WarehousingItemSummary & { displayLabel: string }> = [];
  private itemSearchRequest = 0;
  readonly canEditStockEntries = this.permissionService.hasPermission(app_permissions.warehousing.stock_entry.edit);

  searchForm = this.fb.group({
    keyword: [''],
    item: [null as (WarehousingItemSummary & { displayLabel: string }) | null],
    warehouseId: [null as number | null],
    zoneId: [null as number | null],
    conditionId: [null as number | null],
    receivedFrom: [''],
    receivedTo: ['']
  });

  ngOnInit(): void {
    this.loadStockEntries();
  }

  loadStockEntries(): void {
    this.isLoading = true;
    forkJoin({
      entries: this.warehousingService.getInboundReceipts(),
      clients: this.crmService.getClients(),
      accounts: this.crmService.getAccounts(),
      regions: this.warehousingService.getMasterData('REGION'),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'),
      conditions: this.warehousingService.getMasterData('ITEM_CONDITION')
    }).subscribe({
      next: response => {
        this.allEntries = response.entries.data || [];
        this.applyReceiptFilters();
        this.clients = response.clients || [];
        this.accounts = response.accounts || [];
        this.masterData.REGION = response.regions.data || [];
        this.masterData.WAREHOUSE = companyOwnedWarehouses(response.warehouses.data || []);
        this.masterData.ZONE = response.zones.data || [];
        this.masterData.ITEM_CONDITION = response.conditions.data || [];
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  applySearch(): void {
    this.applyReceiptFilters();
  }

  clearSearch(): void {
    this.searchForm.reset({
      keyword: '',
      item: null,
      warehouseId: null,
      zoneId: null,
      conditionId: null,
      receivedFrom: '',
      receivedTo: ''
    });
    this.itemOptions = [];
    this.itemSearchRequest++;
    this.applyReceiptFilters();
  }

  searchItemOptions(event: { query: string }): void {
    const request = ++this.itemSearchRequest;
    const keyword = event.query.trim();
    if (!keyword) { this.itemOptions = []; return; }
    this.warehousingService.getItemSummaries({ keyword }, 0, 20).subscribe({
      next: response => {
        if (request === this.itemSearchRequest) {
          this.itemOptions = (response.data?.items || []).map(item => ({ ...item, displayLabel: itemIdentityLabel(item) }));
        }
      },
      error: () => { if (request === this.itemSearchRequest) this.itemOptions = []; }
    });
  }

  masterName(type: WarehousingMasterType, id?: number): string {
    return (this.masterData[type] as WarehousingMasterData[]).find(item => item.id === id)?.name || '-';
  }

  displayDate(value?: string | Date): string {
    if (!value) return '-';
    return value instanceof Date ? value.toISOString().slice(0, 10) : String(value);
  }

  receiptLineName(line: WarehousingInboundReceiptLineSummary): string {
    return line.itemName || line.itemNumber || (line.itemId ? `Item #${line.itemId}` : 'Unknown item');
  }

  receiptLineSize(line: WarehousingInboundReceiptLineSummary): string {
    return itemSizeLabel(line) || '—';
  }

  downloadGrn(inboundReceiptId: number, receiptNumber?: string): void {
    this.isLoading = true;
    this.warehousingService.downloadGrnReport(inboundReceiptId).subscribe({
      next: (blob) => {
        this.isLoading = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `grn-${receiptNumber || inboundReceiptId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private applyReceiptFilters(): void {
    const criteria = this.searchForm.getRawValue();
    const keyword = `${criteria.keyword || ''}`.trim().toLowerCase();
    this.entries = this.allEntries.filter(receipt => {
      const receiptDate = receipt.receiptDate ? String(receipt.receiptDate).slice(0, 10) : '';
      const searchable = [receipt.receiptNumber, receipt.referenceNumber, receipt.supplierName,
        receipt.driverName, receipt.vehiclePlateNumber, receipt.warehouseName, receipt.notes]
        .filter(Boolean).join(' ').toLowerCase();
      return (!keyword || searchable.includes(keyword))
        && (!criteria.item?.id || (receipt.lineSummaries || []).some(line => line.itemId === criteria.item?.id))
        && (!criteria.warehouseId || receipt.warehouseId === criteria.warehouseId)
        && (!criteria.receivedFrom || receiptDate >= criteria.receivedFrom)
        && (!criteria.receivedTo || receiptDate <= criteria.receivedTo);
    });
  }
}
